import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { throwError } from 'rxjs/internal/observable/throwError';
import { AmountPerMonth, FinancialByResources } from 'src/app/common/_models/financials';
import { environment } from 'src/environments/environment';
import {
  FinanceEngineStatus,
  FinanceEngineStatusDto,
} from 'src/app/common/_models/financials/financialEngineStatus';

@Injectable()
export class FinancialMetricsService {
  private http = inject(HttpClient);

  private apiBaseUrl = environment.apiBaseUrl;
  private apiFinanceUrl = environment.financeServiceUrl;

  constructor() {}

  getMetrics(metricsBody): Observable<any> {
    return this.http
      .post(this.apiFinanceUrl + 'api/private/v1/finance/metrics', metricsBody)
      .pipe(map((response) => response));
  }

  getMetricsKpi(metricsBody): Observable<any> {
    return this.http
      .post(this.apiFinanceUrl + 'api/private/v1/finance/metrics/kpi', metricsBody)
      .pipe(map((response) => response));
  }

  getMetricsStats(metricsBody): Observable<any> {
    return this.http
      .post(this.apiFinanceUrl + 'api/private/v1/finance/metrics/stats', metricsBody)
      .pipe(map((response) => response));
  }

  getMetricsStatsAnnual(metricsBody): Observable<any> {
    return this.http
      .post(this.apiFinanceUrl + 'api/private/v1/finance/metrics/stats/annual', metricsBody)
      .pipe(map((response) => response));
  }

  getExport(metricsBody: any, type?: string): Observable<any> {
    let queryString = 'type=CulturalMargins&subtype=Metrics';

    if (type) {
      queryString += `&format=${type}`;
    }

    const url = `${this.apiFinanceUrl}api/private/v1/finance/metrics/export?${queryString}`;

    return this.http.post(url, metricsBody, { responseType: 'blob' }).pipe(
      map((res) => res),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  getList(
    year: number,
    commodities?: Array<number>,
    varieties?: Array<any>,
    fields?: Array<any>,
    isSingleFarm = true,
    idFilterFarm = null
  ): Observable<FinancialByResources[]> {
    if (isSingleFarm) {
      let queryString = '?year=' + year.toString();
      if (commodities && commodities.length > 0) {
        commodities.forEach((commodity) => {
          queryString += '&commodity=' + commodity;
        });
      }
      if (varieties && varieties.length > 0) {
        varieties.forEach((variety) => {
          queryString += '&variety=' + variety;
        });
      }
      if (fields && fields.length > 0) {
        fields.forEach((field) => {
          queryString += '&field=' + field;
        });
      }
      return this.http
        .get<any>(this.apiBaseUrl + 'api/private/v1/financial/financialMetrics' + queryString)
        .pipe(map((response) => response));
    } else {
      let queryString = '?year=' + year.toString();
      if (commodities && commodities.length > 0) {
        commodities.forEach((commodity) => {
          queryString += '&commodity=' + commodity;
        });
      }
      if (varieties && varieties.length > 0) {
        varieties.forEach((variety) => {
          queryString += '&variety=' + variety;
        });
      }
      if (fields && fields.length > 0) {
        fields.forEach((field) => {
          queryString += '&field=' + field;
        });
      }
      if (idFilterFarm) {
        queryString += '&companyIds=' + idFilterFarm;
      }
      return this.http
        .get<any>(this.apiBaseUrl + 'api/private/v1/financial/financialMetrics' + queryString)
        .pipe(map((response) => response));
    }
  }

  trasformAmountPerMonthObjectToList = (amountPerMonth: AmountPerMonth) => {
    const result = [];
    if (amountPerMonth) {
      result.push(amountPerMonth.jan);
      result.push(amountPerMonth.feb);
      result.push(amountPerMonth.mar);
      result.push(amountPerMonth.apr);
      result.push(amountPerMonth.may);
      result.push(amountPerMonth.jun);
      result.push(amountPerMonth.jul);
      result.push(amountPerMonth.aug);
      result.push(amountPerMonth.sep);
      result.push(amountPerMonth.oct);
      result.push(amountPerMonth.nov);
      result.push(amountPerMonth.dec);
    }
    return result;
  };

  //? Returns informations about last and pending computations related to the companyId
  getFinanceEngineStatus(): Observable<FinanceEngineStatus> {
    return this.http.get(this.apiFinanceUrl + 'api/private/v1/finance/engine/status').pipe(
      map(
        (res: FinanceEngineStatusDto) =>
          ({
            ...res,
            lastUpdatedAt: res.lastUpdatedAt ? new Date(res.lastUpdatedAt) : null,
          }) as FinanceEngineStatus
      )
    );
  }
}
